import type { EventRecord } from '@romain130492/topchopsticks'
import { bookingModeOf } from '@romain130492/topchopsticks'
import { useI18n } from '../hooks/useI18n'
import { showToast } from '../lib/toast'

/**
 * ALL of an event's detail, in one place.
 *
 * Used by the card (behind "See details") AND the standalone page, so nothing
 * can be visible on one and missing from the other — the same reason the mini
 * program has a single EventDetailsBody.
 *
 * OPEN TO GUESTS. Gating this is what WeChat rejected:
 *   【查看详情】页面未完整浏览、体验页面详情时，即要求授权登录
 * Nothing here is sensitive — the group QR is stripped from the public feed
 * server-side and only arrives with a booked seat.
 */
export default function EventDetailsBody({ event, showPrice = true }: { event: EventRecord; showPrice?: boolean }) {
  const { t, L } = useI18n()
  const address = L(event.address as never)
  const description = L(event.description as never)
  const mode = bookingModeOf(event)

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address)
      showToast({ title: t('ev.addressCopied'), icon: 'success' })
    } catch {
      showToast({ title: t('ev.copyFailed'), icon: 'none' })
    }
  }

  return (
    <div className="mt-3 space-y-2 border-t border-gold-dim pt-3">
      {description ? <p className="text-13 leading-relaxed text-muted">{description}</p> : null}

      {showPrice ? (
        <Row label={t('ev.seat')} value={L(event.price_label as never)} />
      ) : null}

      {address ? (
        <button type="button" onClick={copyAddress} className="flex w-full items-start justify-between gap-3 py-1 text-left">
          <span className="shrink-0 text-sm text-muted">{t('ev.address')}</span>
          <span className="flex items-center gap-1.5 text-right text-sm text-ivory">
            {address}
            <span className="text-gold">⧉</span>
          </span>
        </button>
      ) : null}

      {event.languages?.length ? (
        <Row label={t('ev.languages')} value={event.languages.join(' · ')} />
      ) : null}

      {mode === 'pay_later' ? (
        <p className="pt-1 text-center text-sm text-muted">{t('ev.payLaterNote')}</p>
      ) : null}

      {/* Attendee-only. Absent from the public feed, so a guest never sees it. */}
      {event.wechat_group_qr_url ? (
        <div className="pt-2 text-center">
          <p className="text-2xs uppercase tracking-kicker text-gold">{t('ev.qrTitle')}</p>
          <img src={event.wechat_group_qr_url} alt="" className="mx-auto mt-2 h-40 w-40 object-contain" />
          <p className="mt-1 text-sm text-muted">{t('ev.qrHint')}</p>
        </div>
      ) : null}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="shrink-0 text-sm text-muted">{label}</span>
      <span className="text-right text-sm text-ivory">{value}</span>
    </div>
  )
}
